import useMediaSelectionActions from '@/actions/useMediaSelectionActions'
import useMediaSourceViews from '@/actions/useMediaSourceViews'
import { useAppContext } from '@/AppContext'
import { EnabledIndicator } from '@/components/EnabledIndicator'
import HistogramIcon from '@/CustomIcons/HistogramIcon'
import MIDIIcon from '@/CustomIcons/MIDIIcon'
import MusicNoteIcon from '@/CustomIcons/MusicNoteIcon'
import useMediaSourceActions from '@/functionalities/MediaSources/useMediaSourceActions'
import { queryCurrentMediaSelectionWithFallback } from '@/Logic'
import { env } from '@/services/EnvironmentVariables'
import { Button } from '@/UIComponents2/Button'
import { Checkbox } from '@/UIComponents2/Checkbox'
import { MenuItem } from '@/UIComponents2/MenuButton/MenuButton'
import { useUserContext } from '@/UserContext'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { sessionSettingsTrigger } from '../../SessionPage'
import GearIcon from '@/CustomIcons/GearIcon'
import useComputerAudioSelectionController from '@/services/useComputerAudioSelectionController'
import { MenuButtonContentTitle } from '@/UIComponents2/MenuButton/MenuButtonContentTitle'
import HeadphonesWaveIcon from '@/CustomIcons/HeadphonesWaveIcon'
import { DropdownMenu } from 'radix-ui'

export default function AudioInputsMenu() {
    const { t } = useTranslation()
    const [appState] = useAppContext()
    const [userState] = useUserContext()
    const { audioInputsClassifiedForUi, mediaSources } = useMediaSourceViews()
    const { setMediaSelection, toggleAudioDeviceSelection: handleAudioSelectionListToggle } = useMediaSelectionActions()
    const { loadMidiSources } = useMediaSourceActions()
    const dawBridgeInput = audioInputsClassifiedForUi['museDawBridge'][0]
    const selectedDawAudio = userState.mediaSelection.audioInputs.find(
        (inp) => inp.source.isMuseDAWBridge && dawBridgeInput?.deviceId === inp.deviceId
    )

    const { toggleComputerAudioSelection, isComputerAudioChanging } = useComputerAudioSelectionController()

    const currentMidiSelection = queryCurrentMediaSelectionWithFallback(
        userState.mediaSelection,
        userState.mediaSources,
        'midiInput'
    )

    function isMidiEnabled() {
        const input = userState.mediaSelection.midiInput
        const hasSelection = input !== '' && input !== '_none' && input !== '_undef'
        const enabled = env.OS === 'win32' ? appState.appSettings.forceAccessMidiForWindows : true

        return hasSelection && enabled
    }

    const showMidiMenu = env.IS_ELECTRON && (env.OS === 'win32' ? appState.appSettings.forceAccessMidiForWindows : true)
    const showDawMenu = env.IS_ELECTRON && dawBridgeInput !== undefined

    // TODO: Maybe don't do this...
    useEffect(() => {
        if (showMidiMenu) {
            loadMidiSources()
        }
    }, [loadMidiSources, showMidiMenu])

    return (
        <div className="flex flex-col gap-[18px]">
            <div className="flex flex-col gap-[12px] w-full">
                <MenuButtonContentTitle text={t('Audio Inputs')} icon={MusicNoteIcon} />
                <div className="flex flex-col gap-[12px]">
                    {audioInputsClassifiedForUi.external.map((audioInput) => {
                        const currMediaSelection = userState.mediaSelection
                        const isChecked = currMediaSelection.audioInputs
                            .map((i) => i.deviceId)
                            .includes(audioInput.deviceId)
                        return (
                            <DropdownMenu.Item>
                                <Checkbox
                                    key={audioInput.deviceId}
                                    checked={isChecked}
                                    label={audioInput.label}
                                    onChange={() => {
                                        handleAudioSelectionListToggle(audioInput)
                                    }}
                                />
                            </DropdownMenu.Item>
                        )
                    })}
                </div>
            </div>
            {showMidiMenu && (
                <div className="flex flex-col gap-[12px] w-full">
                    <div className="flex justify-between items-center">
                        <MenuButtonContentTitle text={t('MIDI Devices')} icon={MIDIIcon} />
                        <EnabledIndicator enabled={isMidiEnabled()} />
                    </div>
                    {mediaSources.midiInputs.map((midiInput) => (
                        <DropdownMenu.Item>
                            <Checkbox
                                key={`midi-input-${midiInput.id}`}
                                checked={midiInput.id === currentMidiSelection}
                                label={`${midiInput.name} (${midiInput.manufacturer})`}
                                onChange={() => {
                                    if (midiInput.id === currentMidiSelection) {
                                        setMediaSelection({ midiInput: '_none' })
                                    } else {
                                        setMediaSelection({ midiInput: midiInput.id })
                                    }
                                }}
                            />
                        </DropdownMenu.Item>
                    ))}
                </div>
            )}
            {showDawMenu && (
                <div className="flex flex-col gap-[12px] w-full">
                    <div className="flex justify-between items-center">
                        <MenuButtonContentTitle text={t('DAW Audio')} icon={HistogramIcon} />
                        <EnabledIndicator enabled={!!selectedDawAudio} />
                    </div>
                    <DropdownMenu.Item>
                        <Checkbox
                            key="menuButton_dawAudioCheckbox"
                            label={dawBridgeInput.displayLabel}
                            checked={!!selectedDawAudio}
                            onChange={() => {
                                handleAudioSelectionListToggle(dawBridgeInput)
                            }}
                        />
                    </DropdownMenu.Item>
                </div>
            )}
            {audioInputsClassifiedForUi.system.length > 0 && (
                <div className="flex flex-col gap-[12px] w-full">
                    <MenuButtonContentTitle
                        text={t('Computer Audio')}
                        icon={HeadphonesWaveIcon}
                        className="min-h-[36px]"
                    />
                    {audioInputsClassifiedForUi.system.map((input) => {
                        const currMediaSelection = userState.mediaSelection
                        const isChecked = currMediaSelection.audioInputs.map((i) => i.deviceId).includes(input.deviceId)

                        return (
                            <DropdownMenu.Item>
                                <Checkbox
                                    key={input.deviceId}
                                    checked={isChecked}
                                    label={input.label}
                                    disabled={input.isMuseAudioShare && isComputerAudioChanging}
                                    onChange={() => {
                                        if (input.isMuseAudioShare) {
                                            toggleComputerAudioSelection(input)
                                        } else {
                                            handleAudioSelectionListToggle(input)
                                        }
                                    }}
                                />
                            </DropdownMenu.Item>
                        )
                    })}
                </div>
            )}

            <MenuItem asChild>
                <Button
                    variant="rectangle"
                    className="text-muse-yellow justify-center"
                    onClick={() => {
                        sessionSettingsTrigger.trigger({ tab: 'audio' })
                    }}
                >
                    <GearIcon /> {t('Audio Settings')}
                </Button>
            </MenuItem>
        </div>
    )
}
